/**
 * Google reCAPTCHA v3 provider (GR-004, GR-009).
 *
 * Wraps a subtree with `GoogleReCaptchaProvider` from `react-google-recaptcha-v3`
 * so descendants can call `executeRecaptcha` (via `useRecaptcha`). The provider —
 * and therefore the reCAPTCHA script + badge — is only mounted when:
 *   - the `GOOGLE_RECAPTCHA_ENABLED` site preference is true, AND
 *   - a `GOOGLE_RECAPTCHA_SITE_KEY` is configured.
 *
 * Because we mount the provider per in-scope page (login, registration, checkout
 * login) rather than globally, there is no evidence of reCAPTCHA on any other
 * page, satisfying GR-009.
 */
import React from 'react';
import PropTypes from 'prop-types';
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3';

import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import {isRecaptchaEnabledValue} from '@amblique/king-living/app/components/recaptcha/provider-utils';
import {RECAPTCHA_PREFERENCE_KEYS as KEYS} from '@amblique/king-living/app/constants/recaptcha';

export {isRecaptchaEnabledValue};

/**
 * Conditionally mounts the Google reCAPTCHA provider around its children.
 * When reCAPTCHA is disabled or unconfigured, children render untouched and
 * `executeRecaptcha` stays undefined (verification is then skipped — GR-001).
 */
export const RecaptchaProvider = ({children}) => {
  const enabled = isRecaptchaEnabledValue(useSitePreference(KEYS.ENABLED, false));
  const siteKey = useSitePreference(KEYS.SITE_KEY, '');

  if (!enabled || !siteKey) {
    return <>{children}</>;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={siteKey}
      scriptProps={{async: true, defer: true, appendTo: 'head'}}
    >
      {children}
    </GoogleReCaptchaProvider>
  );
};

RecaptchaProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * HOC that renders a page/component inside `RecaptchaProvider` so the wrapped
 * component (and its descendants) can use `useRecaptcha`. PWA Kit page statics
 * such as `getTemplateName`/`getProps` and `propTypes` are copied onto the
 * wrapper so SSR data fetching and template resolution keep working.
 */
export {RecaptchaErrorMessage} from './recaptcha-error-message';
export {RecaptchaDisclosure} from './recaptcha-disclosure';

export const withRecaptcha = (WrappedComponent) => {
  const ComponentWithRecaptcha = (props) => (
    <RecaptchaProvider>
      <WrappedComponent {...props} />
    </RecaptchaProvider>
  );

  // Preserve PWA Kit / React statics (getTemplateName, getProps, propTypes, ...).
  const name = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  Object.keys(WrappedComponent).forEach((key) => {
    ComponentWithRecaptcha[key] = WrappedComponent[key];
  });

  // Set displayName last so the copy loop above can't clobber it.
  ComponentWithRecaptcha.displayName = `withRecaptcha(${name})`;

  return ComponentWithRecaptcha;
};
